import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Clock, Calendar, ArrowLeft, Phone, Linkedin, Facebook, Link2, Check, MessageCircle } from "lucide-react";
import { Seo } from "./Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP = `https://wa.me/919687944888?text=${encodeURIComponent(
  "Hi Ashish, I'd like to book a free Saturday online financial planning session."
)}`;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

export const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");
  const [copied, setCopied] = useState(false);

  const shareUrl = `https://ashishranade.com/blog/${slug}`;

  const copyLink = () => {
    navigator.clipboard?.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    setStatus("loading");
    axios
      .get(`${API}/posts/${slug}`)
      .then((res) => {
        setPost(res.data);
        setStatus("ok");
      })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  // Inject Article structured data (JSON-LD) for rich Google results
  useEffect(() => {
    if (!post) return;
    const ld = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt || post.title,
      author: { "@type": "Person", name: post.author || "Ashish Ranade" },
      publisher: { "@type": "Organization", name: "Ashish Ranade \u2013 Wealth Educator" },
      datePublished: post.created_at,
      dateModified: post.updated_at || post.created_at,
      mainEntityOfPage: `https://ashishranade.com/blog/${post.slug}`,
      articleSection: post.category,
    };
    let el = document.getElementById("article-jsonld");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "article-jsonld";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(ld);
    return () => {
      const e = document.getElementById("article-jsonld");
      if (e) e.remove();
    };
  }, [post]);

  if (status === "loading") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-500" data-testid="post-loading">
        Loading article…
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6" data-testid="post-notfound">
        <h1 className="text-2xl font-bold text-slate-900">Article not found</h1>
        <p className="mt-2 text-slate-500">This article may have been moved or removed.</p>
        <Link to="/blog" className="mt-6 text-[#1E3A8A] font-semibold hover:underline">
          ← Back to all articles
        </Link>
      </div>
    );
  }

  const paragraphs = post.content.split("\n").filter((p) => p.trim() !== "");

  return (
    <article className="bg-white text-slate-800" data-testid="blog-post-page">
      <Seo
        title={`${post.title} | Ashish Ranade Wealth Educator`}
        description={post.excerpt || post.title}
        canonical={`https://ashishranade.com/blog/${post.slug}`}
      />

      {/* Header */}
      <header className="bg-[#0F172A] text-white pt-16 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/blog"
            data-testid="post-back-link"
            className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="h-4 w-4" /> All articles
          </Link>
          <span className="inline-block bg-[#D4AF37] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full mb-5">
            {post.category}
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
            {post.title}
          </h1>
          <div className="mt-6 flex items-center gap-6 text-sm text-slate-400">
            <span className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" /> {formatDate(post.created_at)}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" /> {post.read_minutes} min read
            </span>
            <span>By {post.author}</span>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="max-w-3xl mx-auto px-6 py-14">
        {/* Share bar */}
        <div className="flex flex-wrap items-center gap-3 mb-10 pb-8 border-b border-slate-100" data-testid="share-bar">
          <span className="text-sm font-semibold text-slate-500 mr-1">Share this:</span>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(post.title + " " + shareUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-whatsapp"
            aria-label="Share on WhatsApp"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 bg-slate-50 hover:bg-[#25D366] hover:text-white border border-slate-200 rounded-full px-4 py-2 transition-colors"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-linkedin"
            aria-label="Share on LinkedIn"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 bg-slate-50 hover:bg-[#0A66C2] hover:text-white border border-slate-200 rounded-full px-4 py-2 transition-colors"
          >
            <Linkedin className="h-4 w-4" /> LinkedIn
          </a>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-facebook"
            aria-label="Share on Facebook"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 bg-slate-50 hover:bg-[#1877F2] hover:text-white border border-slate-200 rounded-full px-4 py-2 transition-colors"
          >
            <Facebook className="h-4 w-4" /> Facebook
          </a>
          <button
            onClick={copyLink}
            data-testid="share-copy"
            aria-label="Copy link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 bg-slate-50 hover:bg-slate-200 border border-slate-200 rounded-full px-4 py-2 transition-colors"
          >
            {copied ? <Check className="h-4 w-4 text-green-600" /> : <Link2 className="h-4 w-4" />}
            {copied ? "Copied!" : "Copy link"}
          </button>
        </div>

        <div className="prose prose-slate max-w-none">
          {paragraphs.map((p, i) => (
            <p key={i} className="text-lg leading-relaxed text-slate-700 mb-6 font-light">
              {p}
            </p>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-14 rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#0F172A] text-white p-8 sm:p-10 text-left">
          <h3 className="text-2xl font-bold">Want personalized guidance?</h3>
          <p className="mt-3 text-slate-300 font-light">
            Book a FREE Saturday online session with Ashish — no
            sales, just clear, conflict-free education tailored to your goals.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-4">
            <Link
              to="/#book"
              data-testid="post-cta-book"
              className="inline-flex items-center justify-center bg-[#D4AF37] hover:bg-[#B4952F] text-white px-6 py-3 rounded-lg font-semibold uppercase tracking-wider text-sm transition-colors"
            >
              Book Free Session
            </Link>
            <a
              href={WHATSAPP}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="post-cta-whatsapp"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white px-6 py-3 rounded-lg font-semibold text-sm transition-colors"
            >
              <Phone className="h-4 w-4" /> WhatsApp Ashish
            </a>
          </div>
        </div>

        <p className="mt-10 text-xs text-slate-400 italic border-l-2 border-[#D4AF37]/50 pl-3.5">
          This article is educational in nature and does not constitute
          investment, tax or financial advice. Please consult a SEBI-registered
          adviser before making financial decisions.        </p>
      </div>
    </article>
  );
};

export default BlogPost;
